import { useMemo, useState } from "react";
import { any, bool, string, arrayOf, oneOfType, object, number } from "prop-types";
import { CircularProgress, Grid, IconButton } from "@material-ui/core";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { useEditorWebsocket, useStores } from "@hooks";
import { HtmlParser, universalDateParser } from "@utils";
import { Alert, Avatar, ChatMessageEditor } from "@components";
import { Pencil, Trash } from "@assets";
import { Clear } from "@material-ui/icons";
import { useEstimateComments, useEstimateEditorSettings } from "@tools";
import { observer } from "mobx-react";

export const ChatMessage = observer(({
  message,
  version,
  viewedType,
  alertRemoval,
  projectTeamMembers,
  isThread,
  path,
}) => {
  const { t } = useTranslation();
  const { userStore } = useStores();
  const { allowCommenting } = useEstimateEditorSettings();
  const socket = useEditorWebsocket();
  const commentStore = useEstimateComments();

  const [ isDeleting, setDeletingState ] = useState(false);
  const [ isSending, setSendingState ] = useState(false);
  const [ editMode, setEditMode ] = useState(false);
  const [ alertVisible, showAlert ] = useState(false);
  
  const { isManager } = userStore;
  const { commenterData } = commentStore;
  const { uuid, parentUuid, createdAt, body, user, isOwn } = message;
  const { fullName, color } = user;

  const allowRemoval = allowCommenting && (isOwn || isManager);
  const allowEdition = allowCommenting && isOwn;

  const useRemoveAlert = alertRemoval || (!isOwn && isManager);

  const parsedDate = useMemo(() => {
    if(!createdAt)
      return new Date();
    const date = universalDateParser(createdAt)
    return new Date(date.getTime() - date.getTimezoneOffset() * 1000 * 60);
  }, []);

  const toggleAlert = () => showAlert(!alertVisible);

  const removeMessage = async () => {
    showAlert(false);
    if (!isDeleting) {
      setDeletingState(true);
      await socket?.requestCommentRemove(uuid, commenterData.uuid, parentUuid, path);
      message.removeSelf(viewedType);
      setDeletingState(false);
    }
  };

  const updateMessage = async (newMessage, login, mentions) => {
    if (!isSending) {
      setSendingState(true);
      await socket?.requestCommentEdit(uuid, commenterData.uuid, version, newMessage, mentions, parentUuid);
      message.updateBody(newMessage, mentions);
      setSendingState(false);
      setEditMode(false);
    }
  };

  return (
    <Grid item container role="listitem" className="p-2">
      <Grid
        item
        container
        justifyContent="space-between"
        alignItems="center"
        className="hoverRow mb-3"
      >
        <Grid item container alignItems="center" className="w-max">
          <Avatar
            fullname={fullName || ""}
            color={color}
            size={32}
          />
          <Grid item container direction="column" className="ml-4 py-0 w-max">
            <p className="text-sm font-bold">
              {isOwn ? t("common.you") : fullName}
            </p>
            <p className="text-xs transparent-2">
              {format(parsedDate, "d MMM, yyyy")}
            </p>
          </Grid>
        </Grid>
        <Grid item container alignItems="center" className="w-max">
          {allowRemoval && (
            <Grid item className="hoverIcon">
              <IconButton
                size="small"
                onClick={useRemoveAlert ? toggleAlert : removeMessage}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <CircularProgress size={14} />
                ) : (
                  <Trash className="text-sm" color="error" />
                )}
              </IconButton>
            </Grid>
          )}
          {allowEdition && (
            <Grid item className="hoverIcon">
              <IconButton
                size="small"
                onClick={() => setEditMode(!editMode)}
                disabled={isSending}
              >
                {editMode ? (
                  <Clear className="text-sm" />
                ) : (
                  <Pencil className="text-sm" />
                )}
              </IconButton>
            </Grid>
          )}
          {useRemoveAlert && (
            <Alert
              isOpen={alertVisible}
              title={t(`views.editor.comments.remove_alert.${isThread ? "thread" : "message"}`)}
              onCancel={toggleAlert}
              onAccept={removeMessage}
              acceptText={t("common.confirm_remove")}
              cancelText={t("common.cancel_remove")}
            />
          )}
        </Grid>
      </Grid>
      <Grid
        item
        container
        alignItems="center"
        aria-label="message content"
        className="ql-root"
      >
        {
          editMode
            ? <ChatMessageEditor
              onCommit={updateMessage}
              placeholder={t("views.editor.comments.placeholder")}
              defaultValue={body}
              projectTeamMembers={projectTeamMembers}
            />
            : HtmlParser(body)
        }
      </Grid>
    </Grid>
  );
})

ChatMessage.propTypes = {
  message: oneOfType([string, arrayOf(any)]),
  version: number.isRequired,
  projectTeamMembers: arrayOf(object),
  viewedType: string.isRequired,
  isThread: bool,
  path: string,
};
